import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  TableSortLabel,
  TablePagination,
  TextField,
  IconButton,
  Button,
  Collapse,
  Typography,
  Chip,
  InputAdornment,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import api from '../utils/apiInterceptor';
import { API_URL } from "../constants/data"


const GrievanceModal = React.lazy(() => import('./Modal'));

const UserDataTable = ({ complaintNumbers, viewMode = 'Category-wise', filters = {}, selectedCompany = null }) => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalLoader, setModalLoader] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [orderBy, setOrderBy] = useState('complaintRegDate');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [expandedData, setExpandedData] = useState({});
  const [loadingExpanded, setLoadingExpanded] = useState({});



  // Fetch complaint details for a specific row
  const fetchComplaintDetails = async (complaintNumber) => {
    if (expandedData[complaintNumber]) return; // Already fetched

    setLoadingExpanded(prev => ({ ...prev, [complaintNumber]: true }));
    try {
      const res = await api.get(
        `${API_URL}/get_complaint_details?complain_number=${complaintNumber}`
      );

      if (res.data) {
        setExpandedData(prev => ({ ...prev, [complaintNumber]: res.data }));
      }
    } catch (error) {
      console.error('Error fetching complaint details:', error);
    } finally {
      setLoadingExpanded(prev => ({ ...prev, [complaintNumber]: false }));
    }
  };

  // Toggle individual row
  const toggleRow = async (complaintNumber) => {
    const isCurrentlyExpanded = expandedRows[complaintNumber];

    if (!isCurrentlyExpanded) {
      await fetchComplaintDetails(complaintNumber);
    }

    setExpandedRows(prev => ({
      ...prev,
      [complaintNumber]: !prev[complaintNumber]
    }));
  };

  // Toggle expand all
  const handleExpandAll = async () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);

    if (newExpandAll) {
      // Expand all rows on current page
      const newExpandedRows = {};
      const promises = paginatedData.map(async (row) => {
        newExpandedRows[row.complainNumber] = true;
        return fetchComplaintDetails(row.complainNumber);
      });

      setExpandedRows(newExpandedRows);
      await Promise.all(promises);
    } else {
      // Collapse all rows
      setExpandedRows({});
    }
  };

  const handleRowClick = async (row) => {
    setSelectedRow(row);
    setModalLoader(true);
    setOpen(true);

    try {
      const res = await api.get(
        `${API_URL}/get_complaint_details?complain_number=${row.id}`
      );

      if (res.data) {
        setModalData(res.data);
      } else {
        console.error('No data received from API');
        setModalData(null);
      }
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      setModalData(null);
    } finally {
      setModalLoader(false);
    }
  };




  // Open modal with already fetched data or fetch new data
  const openModalFromExpanded = async (row) => {
    setSelectedRow(row);

    if (expandedData[row.complainNumber]) {
      setModalData(expandedData[row.complainNumber]);
      setOpen(true);
    } else {
      await handleRowClick(row);
    }
  };


  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    setModalData(null);
  };

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Reset page when complaintNumbers change
  React.useEffect(() => {
    setPage(0);
  }, [complaintNumbers, viewMode]);

  // Fetch paginated data
  React.useEffect(() => {
    const fetchPaginatedData = async () => {
      // Only skip fetch if in company-wise mode and no company selected
      if (viewMode === 'Company-wise' && !selectedCompany && (!complaintNumbers || complaintNumbers.length === 0)) {
        setUserData([]);
        return;
      }

      // For category-wise, need valid complaint numbers
      if (viewMode === 'Category-wise' && (!complaintNumbers || complaintNumbers.length === 0)) {
        setUserData([]);
        return;
      }

      setLoading(true);
      try {
        // Calculate skip and size
        const skip = page * rowsPerPage;
        const size = rowsPerPage;

        const start_date = filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        const end_date = filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

        // Use search-company API for both category-wise and company-wise modes
        const response = await api.post(`${API_URL}/search-company`, {
          query: filters.query || "",
          skip: skip,
          size: size,
          start_date: start_date,
          end_date: end_date,
          value: filters.value || 1,
          purchaseCity: filters.purchaseCity || "All",
          stateName: filters.stateName || "All",
          complaintMode: filters.complaintMode || "All",
          companyName: selectedCompany ? selectedCompany.name : (filters.companyName || "All"),
          complaintStatus: filters.complaintStatus || "All",
          threshold: filters.threshold || 1.5,
          complainNumbers: complaintNumbers && complaintNumbers.length > 0 ? complaintNumbers : ["NA"]
        });

        if (response.data && response.data.grievanceData) {
          // Map the response to match the expected format
          const mappedData = response.data.grievanceData.map(item => ({
            complainNumber: item.id,
            consumerName: item.consumerName || "N/A",
            purchaseCity: item.purchaseCity,
            stateName: item.stateName,
            complaintMode: item.complaintMode,
            complaintStatus: item.complaintStatus,
            complaintRegDate: item.complaintRegDate,
            id: item.id
          }));
          setUserData(mappedData);
          // Set total count from API response for pagination
          setTotalCount(response.data.total_count?.total_count || 0);
        } else {
          setUserData([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPaginatedData();
  }, [
    complaintNumbers,
    page,
    rowsPerPage,
    viewMode,
    selectedCompany,
    // filters.query,
    // filters.value,
    // filters.threshold,
    // filters.companyName,
    // filters.complaintMode,
    // filters.complaintStatus,
    // filters.startDate,
    // filters.endDate,
    // filters.purchaseCity,
    // filters.stateName,
    // filters.complaintNumbers,
  ]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!userData || userData.length === 0) return [];

    // First filter by search query (Client side filtering on the current page)
    let filteredData = userData;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = userData.filter((row) => {
        return (
          (row.consumerName && row.consumerName.toLowerCase().includes(query)) ||
          (row.purchaseCity && row.purchaseCity.toLowerCase().includes(query)) ||

          (row.stateName && row.stateName.toLowerCase().includes(query)) ||
          (row.complaintMode && row.complaintMode.toLowerCase().includes(query)) ||
          (row.complaintStatus && row.complaintStatus.toLowerCase().includes(query)) ||
          (row.complainNumber && row.complainNumber.toLowerCase().includes(query))
        );
      });
    }

    // Then sort the filtered data (Client side sorting on the current page)
    const comparator = (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle date sorting
      if (orderBy === 'complaintRegDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting (case-insensitive)
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (bValue < aValue) {
        return order === 'asc' ? 1 : -1;
      }
      if (bValue > aValue) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    };

    return [...filteredData].sort(comparator);
  }, [userData, order, orderBy, searchQuery]);

  // Since we are fetching paginated data, paginatedData is just sortedData
  const paginatedData = sortedData;

  // Show loading state
  if (loading) {
    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading complaint data...
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  // Show empty state
  if (!userData || userData.length === 0) {
    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          flexDirection: 'column',
          gap: 1
        }}>
          <Typography variant="body1" color="text.secondary">
            No complaint data available
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Select a category from the chart above to view complaints
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  const columns = [
    { id: 'expand', label: '', align: 'left', width: 50 },
    { id: 'complainNumber', label: 'ID', align: 'left' },
    { id: 'consumerName', label: 'Full Name', align: 'left' },
    { id: 'purchaseCity', label: 'City', align: 'left' },
    { id: 'stateName', label: 'State', align: 'left' },
    { id: 'complaintMode', label: 'Complaint Type', align: 'left' },
    { id: 'complaintStatus', label: 'Status', align: 'left' },
    { id: 'complaintRegDate', label: 'Registration Date', align: 'center' },
  ];

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        {/* Controls Bar - All in one line */}
        <Box sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          gap: { xs: 1, sm: 1.5, md: 2 },
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          {/* Expand All / Collapse All Links */}
          <Box sx={{
            flexShrink: 0,
            minWidth: { xs: '100%', sm: 'auto' },
            order: { xs: 3, lg: 1 },
            display: 'flex',
            gap: 1.5,
            alignItems: 'center'
          }}>
            <Typography
              onClick={() => !expandAll && handleExpandAll()}
              sx={{
                fontSize: '0.875rem',
                color: !expandAll ? '#1976d2' : '#999',
                textDecoration: !expandAll ? 'underline' : 'none',
                cursor: !expandAll ? 'pointer' : 'default',
                fontWeight: !expandAll ? 500 : 400,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: !expandAll ? '#1565c0' : '#999',
                  textDecorationThickness: !expandAll ? '2px' : '0px',
                },
                pointerEvents: expandAll ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              Expand All
            </Typography>
            <Typography sx={{ color: '#ccc', fontSize: '0.875rem', userSelect: 'none' }}>|</Typography>
            <Typography
              onClick={() => expandAll && handleExpandAll()}
              sx={{
                fontSize: '0.875rem',
                color: expandAll ? '#1976d2' : '#999',
                textDecoration: expandAll ? 'underline' : 'none',
                cursor: expandAll ? 'pointer' : 'default',
                fontWeight: expandAll ? 500 : 400,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: expandAll ? '#1565c0' : '#999',
                  textDecorationThickness: expandAll ? '2px' : '0px',
                },
                pointerEvents: !expandAll ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              Collapse All
            </Typography>
          </Box>

          {/* Search Bar */}
          <TextField
            placeholder="Search by ID, name, city, or state..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flexGrow: 1,
              minWidth: { xs: '100%', sm: '200px', lg: '250px' },
              order: { xs: 1, lg: 2 },
              '& .MuiOutlinedInput-root': {
                height: '40px',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              flexShrink: 0,
              order: { xs: 2, lg: 3 },
              minWidth: { xs: '100%', sm: 'auto' },
              // border: '1px solid rgba(0, 0, 0, 0.23)', 
              borderRadius: '4px',
              backgroundColor: '#fff',
              overflow: 'hidden',
              '& .MuiTablePagination-toolbar': {
                minHeight: '38px',
                height: '38px',
                padding: 0,
                paddingLeft: { xs: 1, sm: 1.5 },
                paddingRight: { xs: 0.5, sm: 1 },
              },
              '& .MuiTablePagination-spacer': {
                flex: '0 0 auto',
              },
              '& .MuiTablePagination-selectLabel': {
                margin: 0,
                fontSize: '0.875rem',
                display: { xs: 'none', sm: 'block' },
              },
              '& .MuiTablePagination-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
                paddingLeft: '4px',
                paddingRight: '4px',
                fontSize: '0.875rem',
              },
              '& .MuiTablePagination-selectIcon': {
                top: 'calc(50% - 12px)',
              },
              '& .MuiTablePagination-displayedRows': {
                margin: 0,
                fontSize: '0.875rem',
              },
              '& .MuiTablePagination-actions': {
                marginLeft: { xs: 0.5, sm: 1 },
                '& .MuiIconButton-root': {
                  padding: '8px',
                }
              }
            }}
          />
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ width: column.width }}
                >
                  {column.id !== 'expand' && (
                    <TableSortLabel
                      active={true}
                      direction={orderBy === column.id ? order : 'desc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <React.Fragment key={row.complainNumber}>
                {/* Main Row */}
                <TableRow
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(row.complainNumber);
                  }}
                >
                  {/* Expand Button Cell */}
                  <TableCell sx={{ width: 50, padding: '6px' }}>
                    <IconButton
                      size="small"
                    >
                      {expandedRows[row.complainNumber] ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  {/* Data Cells */}
                  <TableCell>{row.complainNumber}</TableCell>
                  <TableCell>{row.consumerName}</TableCell>
                  <TableCell>{row.purchaseCity}</TableCell>

                  <TableCell>{row.stateName}</TableCell>
                  <TableCell>{row.complaintMode}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.complaintStatus ? row.complaintStatus : "NA"}
                      color={
                        row.complaintStatus?.toLowerCase() === 'dispose off' ||
                          row.complaintStatus?.toLowerCase() === 'closed'
                          ? 'success' : row.complaintStatus?.toLowerCase() === "" || row.complaintStatus === null
                            ? 'error'
                            : 'warning'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {new Date(row.complaintRegDate).toLocaleDateString()}
                  </TableCell>

                </TableRow>

                {/* Expandable Row */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                    <Collapse in={expandedRows[row.complainNumber]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                        {loadingExpanded[row.complainNumber] ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : expandedData[row.complainNumber] ? (
                          <>
                            <Box sx={{
                              display: 'flex',
                              gap: 3,
                              flexDirection: { xs: 'column', md: 'row' }
                            }}>
                              {/* Left Side - Subject and Agent Remark */}
                              <Box sx={{ flex: 1 }}>
                                {expandedData[row.complainNumber].frequentlyOccuredProblem && (
                                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      Subject:
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}>
                                      {expandedData[row.complainNumber].frequentlyOccuredProblem}
                                    </Typography>
                                  </Box>
                                )}

                                {expandedData[row.complainNumber].agentRemark && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      Agent Remark:
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}>
                                      {expandedData[row.complainNumber].agentRemark}
                                    </Typography>
                                  </Box>
                                )}
                                {/* {expandedData[row.complaintNumber].userexperience && ( */}
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    User Experience:
                                  </Typography>
                                  <Typography variant="body2" sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}>
                                    {expandedData[row.complainNumber].userexperience || "N/A"}
                                  </Typography>
                                </Box>
                                {/* )} */}
                              </Box>

                              {/* Right Side - Company Name and Status */}
                              <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                              }}>
                                {/* Company Name - Inline layout */}
                                {(expandedData[row.complainNumber].converganceCompanyName || expandedData[row.complainNumber].nonCoverganeceCompanyName) && (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      Company:
                                    </Typography>
                                    <Typography variant="body2">
                                      {expandedData[row.complainNumber].converganceCompanyName &&
                                        expandedData[row.complainNumber].converganceCompanyName.trim() !== '' &&
                                        expandedData[row.complainNumber].converganceCompanyName.toLowerCase() !== 'n/a'
                                        ? expandedData[row.complainNumber].converganceCompanyName
                                        : expandedData[row.complainNumber].nonCoverganeceCompanyName &&
                                          expandedData[row.complainNumber].nonCoverganeceCompanyName.trim() !== '' &&
                                          expandedData[row.complainNumber].nonCoverganeceCompanyName.toLowerCase() !== 'n/a'
                                          ? expandedData[row.complainNumber].nonCoverganeceCompanyName
                                          : 'N/A'}
                                    </Typography>
                                    <Chip
                                      label={
                                        expandedData[row.complainNumber].converganceCompanyName &&
                                          expandedData[row.complainNumber].converganceCompanyName.trim() !== '' &&
                                          expandedData[row.complainNumber].converganceCompanyName.toLowerCase() !== 'n/a'
                                          ? "Convergence"
                                          : "Non-Convergence"
                                      }
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '20px',
                                        backgroundColor:
                                          expandedData[row.complainNumber].converganceCompanyName &&
                                            expandedData[row.complainNumber].converganceCompanyName.trim() !== '' &&
                                            expandedData[row.complainNumber].converganceCompanyName.toLowerCase() !== 'n/a'
                                            ? '#e8f5e9' : '#fff3e0',
                                        color:
                                          expandedData[row.complainNumber].converganceCompanyName &&
                                            expandedData[row.complainNumber].converganceCompanyName.trim() !== '' &&
                                            expandedData[row.complainNumber].converganceCompanyName.toLowerCase() !== 'n/a'
                                            ? '#2e7d32' : '#e65100',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Complaint Status - Inline layout with Chip */}
                                {expandedData[row.complainNumber].complaintStatus && (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      Complaint Status:
                                    </Typography>
                                    <Chip
                                      label={`${expandedData[row.complainNumber].complaintStatus}`}
                                      size="small"
                                      color={
                                        expandedData[row.complainNumber].complaintStatus?.toLowerCase() === 'dispose off' ||
                                          expandedData[row.complainNumber].complaintStatus?.toLowerCase() === 'closed'
                                          ? 'success'
                                          : 'warning'
                                      }
                                      variant="outlined"
                                    />
                                    {expandedData[row.complainNumber].companyStatus.trim() !== '' &&
                                      !['na', 'n/a'].includes(expandedData[row.complainNumber].companyStatus.toLowerCase()) && (
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          - by Company
                                        </Typography>
                                      )}
                                  </Box>
                                )}

                              </Box>
                            </Box>

                            {/* Show More Button */}
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => openModalFromExpanded(row)}
                              >
                                Show More
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No details available
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {open && (
        <>
          {modalLoader ? (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.2)',
                zIndex: 9999,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            modalData && (
              <GrievanceModal
                open={open}
                onClose={handleClose}
                modalData={modalData}
                modalLoader={modalLoader}
              />
            )
          )}
        </>
      )}
    </>
  );
};

export default UserDataTable;